import { useState, useEffect } from 'react';
import { VStack, Heading, Text, ArrowCta, FormCard } from 'components/ui';
import { Spinner } from '@chakra-ui/react';
import { useTracking } from '../../hooks/useTracking';
import { useLocation } from 'react-router-dom';

import useRegisterPost from '../../api/register/mutations/useRegisterPost';
import HeaderNav from '../HeaderNav/HeaderNav';
import useEligibilityRouting from '../../hooks/useEligibilityRouting';
import { useSignUpIdentity } from '../../hooks/useSignUpIdentity';
import useAuthenticatedUser from '../../api/user/useAuthenticatedUser';
import useUserRegisterPut from '../../api/user/mutations/useUserRegisterPut';
import { Link } from '../ui/Link';
import { StickyCtaLayout } from '../ui/StickyCtaLayout';
import PhoneNumberForm from 'components/PhoneNumberView/PhoneNumberForm';

const TRACK_PHONE_COLLECTION = 'phone_collection';

export const PhoneNumber = () => {
  const { page: pageView } = useTracking();
  const { search, state } = useLocation();
  const [skipEligibilityCheck, setSkipEligibilityCheck] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [agreeChecked, setAgreeCheck] = useState(false);
  const { mutation } = useRegisterPost();
  const { mutation: updateUserRegistrationMutation } = useUserRegisterPut();
  const { user: authenticatedUser, isLoading: isUserLoading } =
    useAuthenticatedUser();

  const signUpIdentity = useSignUpIdentity();

  const userId = authenticatedUser?.id || signUpIdentity.userId;

  const isEligibilityEnabled =
    updateUserRegistrationMutation.isSuccess ||
    mutation.isSuccess ||
    skipEligibilityCheck;

  const { isPending } = useEligibilityRouting({
    userId,
    state: { ...state, ...signUpIdentity },
    search,
    enable: isEligibilityEnabled,
  });

  const isLoading =
    (isEligibilityEnabled && isPending) ||
    mutation.isLoading ||
    updateUserRegistrationMutation.isLoading;

  useEffect(() => {
    if (!isUserLoading && authenticatedUser) {
      setPhoneNumber(authenticatedUser.phone_number);
    } else {
      const storedPhone = localStorage.getItem('USER_PHONE');
      if (storedPhone) setPhoneNumber(storedPhone);
    }
  }, [isUserLoading, authenticatedUser]);

  useEffect(() => {
    pageView(TRACK_PHONE_COLLECTION);
  }, [pageView]);

  const handleNext = async () => {
    localStorage.setItem('USER_PHONE', phoneNumber);

    try {
      if (authenticatedUser) {
        await updateUserRegistrationMutation.mutateAsync({
          phone_number: phoneNumber,
        });
      } else {
        await mutation.mutateAsync({
          email: signUpIdentity.email,
          phoneNumber,
        });
      }
    } catch (error) {
      console.error('failed to save phone number:', error);
    }
  };

  const renderContent = () => {
    return (
      <>
        <Heading textStyle="heading.h2">What's your phone number?</Heading>

        <Text
          color={'secondary.200'}
          textStyle="body.sub"
          textAlign={'center'}
          maxW={'proseWidth'}
          mt={4}
        >
          We’ll text you with appointment reminders and other important updates
          about your program. You can opt out at any time.
        </Text>

        <FormCard w={'100%'} maxW={'formWidth'} mt={8}>
          <PhoneNumberForm
            agreed={agreeChecked}
            phoneNumber={phoneNumber}
            setAgreed={setAgreeCheck}
            setPhoneNumber={setPhoneNumber}
          />
        </FormCard>
      </>
    );
  };

  return (
    <StickyCtaLayout
      actionArea={
        <>
          <ArrowCta
            alignSelf={'center'}
            disabled={phoneNumber?.length !== 12 || isLoading || !agreeChecked}
            onClick={handleNext}
          >
            {isLoading ? <Spinner size="sm" /> : 'Next'}
          </ArrowCta>

          <Link
            color="secondary.500"
            fontWeight={700}
            isExternal={false}
            mt={4}
            opacity={isLoading ? 0.5 : 1}
            onPress={() => {
              if (!isLoading) {
                setSkipEligibilityCheck(true);
              }
            }}
          >
            Skip this step
          </Link>
        </>
      }
      header={<HeaderNav />}
      maxWidth="formWidth"
    >
      <VStack alignItems={'center'} gap={4} w="100%">
        {renderContent()}

        {isLoading && (
          <Text fontSize="lg" mt={4}>
            Setting up your visit options. This takes a few seconds.
          </Text>
        )}
      </VStack>
    </StickyCtaLayout>
  );
};

export default PhoneNumber;
